import { useState } from "react";
import { Bus } from "lucide-react";
import { Button, Icon, Sheet } from "@cntrl/ui";
import { useIsPro } from "../../app/bandContext.js";
import { useMyBunk } from "../../app/hooks/useBuses.js";
import { MyBunkCard } from "../buses/MyBunkCard.js";
import { FleetPager } from "../buses/FleetPager.js";

/**
 * The Pro "My Bus" entry on Day of Show — a header bus icon that opens a bottom sheet (ART-174 §My Bus). It shows
 * whenever the band runs a COACH, not only when the caller holds a bunk (ART-148, the legacy `requireBunk=false`
 * reasoning): the driver's number and the door code are bus-level facts an unbunked rider still needs, and gating
 * the icon on a bunk made "not Pro", "no fleet" and "not assigned yet" all look like the same nothing.
 *
 * WHAT the sheet holds follows the SERVED `canEditFleet` verdict: an AV/TM seat swipes the whole fleet with the
 * full bunk chart (the per-seat ruling's audience, PO 2026-09-12); everyone else gets their own `MyBunkCard`.
 * Both are theme-aware, so inside the forced-dark DOS the sheet renders dark for free. Nothing on a Standard plan.
 *
 * It sits on EVERY Day of Show state — a show's detail header AND the list / no-shows view (ART-148, legacy
 * parity): a bunk belongs to the tour leg, not to one date, so reaching it must not need a show to open.
 */
export function BusSheetButton() {
  const isPro = useIsPro();
  const bunk = useMyBunk(isPro);
  const [open, setOpen] = useState(false);
  const info = bunk.data;
  if (!isPro || !info || info.coaches === 0) return null;
  const pager = info.canEditFleet;
  const title = pager
    ? (info.coaches > 1 ? `${info.coaches} buses · swipe` : "The bus")
    : "Your bus";
  return (
    <>
      <Button variant="ghost" size="sm" aria-label="Your bunk" onClick={() => setOpen(true)}>
        <Icon icon={Bus} size={16} />
      </Button>
      {open && (
        <Sheet title={title} onClose={() => setOpen(false)}>
          {pager ? <FleetPager /> : <MyBunkCard emptyMode="note" />}
        </Sheet>
      )}
    </>
  );
}
